import React from "react";
import "./weekNavigator.scss";

export interface WeekNavigatorProps {
  /** Start date of the displayed range */
  startDate: Date;
  /** End date of the displayed range */
  endDate: Date;
  /** Label below the date range (e.g. "Working week · Changes apply to this week only") */
  subtitle?: string;
  /** Called when the user clicks the previous ( ‹ ) arrow */
  onPrev?: () => void;
  /** Called when the user clicks the next ( › ) arrow */
  onNext?: () => void;
  /** Disable the previous arrow */
  prevDisabled?: boolean;
  /** Disable the next arrow */
  nextDisabled?: boolean;
  /** Custom class applied to the root element */
  className?: string;
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatDateRange(start: Date, end: Date): string {
  const startMonth = MONTH_NAMES[start.getMonth()];
  const endMonth = MONTH_NAMES[end.getMonth()];
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  // Same month & year → "May 5–11, 2026"
  if (startMonth === endMonth && startYear === endYear) {
    return `${startMonth} ${startDay}–${endDay}, ${startYear}`;
  }

  // Same year, different month → "May 28–Jun 3, 2026"
  if (startYear === endYear) {
    return `${startMonth} ${startDay}–${endMonth} ${endDay}, ${startYear}`;
  }

  // Different years → "Dec 29, 2025–Jan 4, 2026"
  return `${startMonth} ${startDay}, ${startYear}–${endMonth} ${endDay}, ${endYear}`;
}

const WeekNavigator: React.FC<WeekNavigatorProps> = ({
  startDate,
  endDate,
  subtitle,
  onPrev,
  onNext,
  prevDisabled = false,
  nextDisabled = false,
  className = "",
}) => {
  const dateLabel = formatDateRange(startDate, endDate);

  return (
    <div className={`week-navigator ${className}`.trim()} role="navigation" aria-label="Week navigation">
      <button
        className="week-navigator__arrow week-navigator__arrow--prev"
        onClick={onPrev}
        disabled={prevDisabled}
        aria-label="Previous week"
        type="button"
      >
        &#8249;
      </button>

      <div className="week-navigator__center">
        <span className="week-navigator__date-range">{dateLabel}</span>
        {subtitle && (
          <span className="week-navigator__subtitle">{subtitle}</span>
        )}
      </div>

      <button
        className="week-navigator__arrow week-navigator__arrow--next"
        onClick={onNext}
        disabled={nextDisabled}
        aria-label="Next week"
        type="button"
      >
        &#8250;
      </button>
    </div>
  );
};

export default WeekNavigator;